import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type ActionResult } from '@/lib/api'
import { centeredSwal, showError } from '@/lib/alert'
import { usePageTitle } from '@/hooks/usePageTitle'
import { fetchOtpContext, sendOtp, type OtpPurpose } from '@/features/account/otp'

/**
 * Port of Views/Account/ConfirmEmail.cshtml and ConfirmEmailRegist.cshtml,
 * plus confirmEmail.js and confirmEmailRegist.js.
 *
 * The two views were byte-identical apart from the resend link text
 * ("Resend Code" vs "Resend OTP"), and the two 570-line scripts differed only in
 * where they redirected afterwards. They are one component with two props.
 *
 * SECURITY: the OTP is no longer compared in the browser. The original rendered
 * the code into a hidden `#OtpId` field and matched it client-side, so it was
 * readable in DevTools and the check was skippable outright — see the note on
 * POST account/confirm-email in the BFF. The typed code now goes to the server,
 * which compares it against the value it issued. Nothing changes for a user who
 * reads the code from their email.
 */
interface ConfirmOtpPageProps {
  title: string
  /** "Resend Code" on the forgot-password flow, "Resend OTP" on registration. */
  resendLabel: string
  /** Which flow this page serves, so a resend re-issues the same kind of code. */
  purpose: OtpPurpose
  /** Where to go once the address is confirmed, if the server offers nothing. */
  fallbackRedirect: string
  /** Where to send a user who arrived with no verification in progress. */
  restartRedirect: string
  restartMessage: string
}

const OTP_LENGTH = 4
const COUNTDOWN_SECONDS = 300

export function ConfirmOtpPage({
  title,
  resendLabel,
  purpose,
  fallbackRedirect,
  restartRedirect,
  restartMessage,
}: ConfirmOtpPageProps): JSX.Element {
  usePageTitle(title, 'auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const hash = searchParams.get('hash') ?? ''

  const [digits, setDigits] = useState<string[]>(Array<string>(OTP_LENGTH).fill(''))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isInvalid, setIsInvalid] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS)

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['account', 'otp-context', hash],
    queryFn: () => fetchOtpContext(hash),
    retry: false,
  })

  const email = data?.email ?? ''

  // "Email not found. Please start the ... process again." — the original waited
  // 3s and then bounced. Same, once the lookup has resolved.
  useEffect(() => {
    if (isLoading || !data) return
    if (data.success && email) return

    void (async () => {
      await showError(restartMessage)
      navigate(restartRedirect)
    })()
  }, [isLoading, data, email, navigate, restartRedirect, restartMessage])

  // The `#countdown` ticker. It gates the resend link, as in the original.
  useEffect(() => {
    if (secondsLeft <= 0) return
    const timer = window.setTimeout(() => setSecondsLeft((value) => value - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [secondsLeft])

  function setDigitAt(index: number, value: string): void {
    setDigits((previous) => {
      const next = [...previous]
      next[index] = value
      return next
    })
  }

  /** initOTPInputs: digits only, auto-advance, backspace steps back, paste fills all four. */
  function handleChange(index: number, raw: string): void {
    const value = raw.slice(-1)
    if (value !== '' && !/^\d$/.test(value)) return

    setIsInvalid(false)
    setDigitAt(index, value)
    if (value && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus()
  }

  function handleKeyDown(index: number, event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>): void {
    event.preventDefault()
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (pasted.length !== OTP_LENGTH) return
    setDigits(pasted.split(''))
    setIsInvalid(false)
    inputRefs.current[OTP_LENGTH - 1]?.focus()
  }

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault()

    const code = digits.join('')
    if (code.length !== OTP_LENGTH) {
      setIsInvalid(true)
      return
    }

    setIsExpired(false)
    setIsSubmitting(true)
    try {
      const response = await api.post<ActionResult & { expired?: boolean }>(
        '/api/ecareer/account/confirm-email',
        { OtpCode: code },
      )

      if (!response.success) {
        if (response.expired) {
          setIsExpired(true)
          return
        }
        setIsInvalid(true)
        await showError(response.message ?? 'The code you entered is incorrect.')
        return
      }

      await centeredSwal({
        icon: 'success',
        title: 'Email Confirmed!',
        text: 'Your email has been successfully confirmed.',
        timer: 2000,
        showConfirmButton: false,
        allowOutsideClick: false,
        timerProgressBar: true,
      })
      navigate(response.redirectUrl ?? fallbackRedirect)
    } catch (error) {
      // The original showed "Email Confirmed!" and moved on even when the call
      // failed. It does not any more: a failure is a failure.
      await showError(
        error instanceof Error ? error.message : 'Verification failed. Please try again.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleResend(event: React.MouseEvent): Promise<void> {
    event.preventDefault()
    if (secondsLeft > 0 || !email) return

    try {
      const result = await sendOtp(email, purpose)
      if (!result.success) {
        await showError(result.message ?? 'Failed to resend the code. Please try again.')
        return
      }

      setDigits(Array<string>(OTP_LENGTH).fill(''))
      setIsExpired(false)
      setIsInvalid(false)
      setSecondsLeft(COUNTDOWN_SECONDS)
      await refetch()

      await centeredSwal({
        icon: 'success',
        title: 'OTP Sent!',
        text: 'Please check your email for the verification code.',
        timer: 2000,
        showConfirmButton: false,
        timerProgressBar: true,
      })
    } catch {
      await showError('Failed to resend the code. Please try again.')
    }
  }

  return (
    <>
      <div className="text-center">
        <img
          src="/lib/images/authentication/img-auth-code-varify.png"
          alt="Code Verification"
          className="img-fluid mb-3"
        />
        <h4 className="f-w-500 mb-1">Please confirm with OTP</h4>
        <p className="mb-0">
          We&apos;ve sent you code on <label id="emailOTP">{email}</label>
        </p>
        <p className="mb-3">
          Did not receive the email?{' '}
          <a
            href="#"
            className={`link-primary ms-1${secondsLeft > 0 ? ' disabled text-muted' : ''}`}
            id="resendBtn"
            onClick={(event) => void handleResend(event)}
          >
            {resendLabel}
          </a>
        </p>
        {secondsLeft > 0 && (
          <p className="text-muted mb-3" id="timerText">
            Resend OTP in <span id="countdown">{secondsLeft}</span> seconds
          </p>
        )}
      </div>

      {isExpired && (
        <div className="alert alert-danger" role="alert" id="otpExpired">
          Your OTP has expired. Please request a new one to continue.
        </div>
      )}

      <form id="confirmEmailForm" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className="row my-4 g-3 text-center">
          {digits.map((digit, index) => (
            // eslint-disable-next-line react/no-array-index-key -- fixed-length positional inputs
            <div className="col" key={index}>
              <input
                ref={(element) => {
                  inputRefs.current[index] = element
                }}
                type="text"
                inputMode="numeric"
                className={`form-control text-center otp-input${isInvalid ? ' is-invalid' : ''}`}
                id={`otp${index + 1}`}
                name={`otp${index + 1}`}
                maxLength={1}
                pattern="[0-9]"
                required
                autoFocus={index === 0}
                value={digit}
                onChange={(event) => handleChange(index, event.target.value)}
                onKeyDown={(event) => handleKeyDown(index, event)}
                onPaste={handlePaste}
              />
            </div>
          ))}
        </div>

        <div className="d-grid mt-4">
          <button type="submit" className="btn btn-primary" id="confirmBtn" disabled={isSubmitting}>
            {isSubmitting ? 'Verifying...' : 'Confirm'}
          </button>
        </div>
      </form>
    </>
  )
}
